import React, { useEffect, useMemo, useRef, useState } from "react";
import { get } from "aws-amplify/api";

export default function PatientSelectTypeahead({
  orgUuid,
  pccUserId,
  pccAccessToken,     // (optional) pass if your API requires Authorization
  selectedFacilityId,
  onSelect,           // ({ patientId, name, dob, mrn, facId }) => void
  disabled = false,
  placeholder = "Search patient by name…",
  minChars = 2,       // require 2+ chars before querying
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [highlight, setHighlight] = useState(-1);

  const boxRef = useRef(null);
  const abortRef = useRef(null);               // AbortController for in-flight fetch
  const lastFacilityRef = useRef(null);        // Track facility used for last request

  // Debounce util
  const debounceMs = 250;
  const debounced = useMemo(() => {
    let t;
    return (val, cb) => { clearTimeout(t); t = setTimeout(() => cb(val), debounceMs); };
  }, []);

  // Cleanup aborter on unmount
  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  const runQuery = async (query) => {
    // Guard: need facility + enough characters
    if (!selectedFacilityId || !query || query.trim().length < minChars) {
      setItems([]); setOpen(false); setLoading(false); setError("");
      return;
    }

    setLoading(true);
    setError("");
    try {
      // cancel any in-flight request
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // remember which facility this request targets
      lastFacilityRef.current = String(selectedFacilityId);

      const res = await get({
        apiName: "pccapi",
        path: "/patients",
        options: {
          // Amplify v6 uses queryParams
          queryParams: {
            query: query.trim(),
            facId: String(selectedFacilityId),
            limit: "20",
          },
          headers: {
            "Content-Type": "application/json",
            "X-PCC-OrgUuid": orgUuid,
            "X-PCC-UserId": pccUserId,
            "X-PCC-FacilityId": String(selectedFacilityId),  // send facility as header too
            ...(pccAccessToken ? { "Authorization": `Bearer ${pccAccessToken}` } : {})
          },
          signal: controller.signal,
        },
      }).response;

      const data = await res.body.json();

      // ignore stale response if facility changed while awaiting
      if (lastFacilityRef.current !== String(selectedFacilityId)) return;

      const list = Array.isArray(data.items) ? data.items : [];
      setItems(list);
      setOpen(list.length > 0 || (!loading && q.trim().length >= minChars)); // keep open to show "No matches"
      setHighlight(list.length ? 0 : -1);
    } catch (e) {
      if (e?.name !== "AbortError") {
        setError("Failed to load patients");
        setItems([]); setOpen(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // On facility change: reset, cancel, and re-run current query if long enough
  useEffect(() => {
    setItems([]); setOpen(false); setHighlight(-1); setError("");
    if (abortRef.current) abortRef.current.abort();

    if (selectedFacilityId && q.trim().length >= minChars) {
      // immediate fetch; bypass debounce so the UI feels responsive
      runQuery(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacilityId]);

  // Debounced query fetch on input (when facility is selected)
  useEffect(() => {
    if (!selectedFacilityId) return;
    debounced(q, runQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, selectedFacilityId]);

  // close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (item) => {
    onSelect?.(item);
    setQ(item.name || "");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || items.length === 0) {
      // allow Enter to force a query if enough chars typed
      if (e.key === "Enter" && q.trim().length >= minChars && !loading) {
        e.preventDefault();
        runQuery(q);
      }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(items.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(items[highlight]); }
    else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
    else if (e.key === "Home") { e.preventDefault(); setHighlight(0); }
    else if (e.key === "End") { e.preventDefault(); setHighlight(items.length - 1); }
  };

  const tooShort = q.trim().length > 0 && q.trim().length < minChars;

  return (
    <div ref={boxRef} style={{ position: "relative", width: "100%" }}>
      <input
        type="text"
        value={q}
        disabled={disabled || !selectedFacilityId}
        placeholder={selectedFacilityId ? placeholder : "Select a facility first…"}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => {
          // only open if we have relevant state (tooShort hint or results/error)
          if (tooShort || error || items.length > 0) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        className="field-input"
        style={{ width: "100%" }}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls="patient-typeahead-listbox"
        role="combobox"
      />
      {/* helper texts */}
      {!disabled && selectedFacilityId && tooShort && (
        <div className="text-muted" style={{ marginTop: 4 }}>
          Type at least {minChars} characters to search.
        </div>
      )}
      {loading && <div className="text-muted" style={{ marginTop: 4 }}>Loading…</div>}
      {error && <div className="text-danger" style={{ marginTop: 4 }}>{error}</div>}

      {open && (
        <div
          id="patient-typeahead-listbox"
          style={{
            position: "absolute",
            zIndex: 10,
            top: "100%",
            left: 0,
            right: 0,
            maxHeight: 260,
            overflowY: "auto",
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 6,
            boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
            marginTop: 4,
          }}
          role="listbox"
        >
          {items.length === 0 && !loading && !error && q.trim().length >= minChars && (
            <div style={{ padding: "8px 10px", fontSize: 12, color: "#888" }}>
              No matches.
            </div>
          )}
          {items.map((it, idx) => (
            <div
              key={`${it.patientId ?? it.id ?? 'row'}-${idx}`}
              role="option"
              aria-selected={idx === highlight}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(it)}
              onMouseEnter={() => setHighlight(idx)}
              style={{
                padding: "8px 10px",
                cursor: "pointer",
                background: idx === highlight ? "#f5f5f5" : "transparent",
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
              }}
              title={it.name}
            >
              <span>{it.name}</span>
              <span className="text-muted" style={{ fontSize: 12 }}>
                {(it.dob || it.dateOfBirth || "DOB n/a")} • MRN {(it.mrn || it.medicalRecordNumber || "n/a")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
